/**
 * The extra search params: their empty state, how the URL holds them, and what
 * goes on the wire.
 *
 * Pure over `declaredParams`, so all of this tests without a store or a DOM.
 * The declaration is the only thing that says which URL params are search params
 * and what arity each one has, so every function here is driven by it rather
 * than by what happens to be present in a URL or an object.
 *
 * Names carry the direction, as `Params` does in PHP: `paramsFromUrl` reads,
 * `toQueryString` and `writeToUrl` write.
 */

import { declaredParams, urlKey } from "./config";
import type { SearchParamValues } from "./types";

/** Every declared param at its empty value, with the right arity. */
export function emptyParams(): SearchParamValues {
  const out: SearchParamValues = {};
  for (const [name, arity] of declaredParams) {
    out[name] = arity === "many" ? [] : "";
  }
  return out;
}

/**
 * The declared params as a URL holds them.
 *
 * Undeclared params are ignored, which is what keeps `utm_source`, `fbclid` and
 * a theme's own parameters off the wire. Arity comes from the declaration and
 * not from how many values happen to be present, so a checkbox group whose URL
 * carries one value still sees an array.
 *
 * Read tolerantly, written canonically: a repeatable param is looked for under
 * `topic[]` first and under the bare `topic` only if that found nothing.
 */
export function paramsFromUrl(url: URL): SearchParamValues {
  const out: SearchParamValues = {};

  for (const [name, arity] of declaredParams) {
    const canonical = url.searchParams
      .getAll(urlKey(name))
      .filter((v) => v !== "");

    // The bracketless spelling, which is what a hand-edited url is likely to
    // carry. Consulted only when the canonical key found nothing, so a value
    // present in both is not counted twice.
    const legacy =
      arity === "many" && canonical.length === 0
        ? url.searchParams.getAll(name).filter((v) => v !== "")
        : [];

    const values = canonical.length ? canonical : legacy;
    out[name] = arity === "many" ? values : (values[0] ?? "");
  }

  return out;
}

/**
 * Replaces the parameters a search owns, and leaves every other one alone.
 *
 * The one implementation of "this url describes this search", used both for the
 * address bar and for any link built out of live store state. Only the declared
 * names are touched: clearing `url.search` outright would take the theme's own
 * parameters with it, `classic` and campaign tags included.
 *
 * Both spellings are removed, or a tolerated `?topic=waste` would survive
 * alongside the `topic[]=waste` written next to it. That is also where a
 * bracketless url becomes canonical. For a single-valued param the two keys are
 * the same string and the second delete does nothing.
 *
 * Mutates and returns the URL it was given, which is what the callers want:
 * `pushState` takes it as-is, and `toString()` is a link.
 */
export function writeToUrl(
  url: URL,
  query: string,
  params: SearchParamValues,
): URL {
  url.searchParams.delete("s");
  for (const name of declaredParams.keys()) {
    url.searchParams.delete(urlKey(name));
    url.searchParams.delete(name);
  }
  for (const [name, value] of toQueryString(query, params)) {
    url.searchParams.append(name, value);
  }

  return url;
}

/**
 * `s` plus the declared params, spelled canonically, so a repeatable param
 * carries `[]`. Empty values are an unset filter and are dropped.
 *
 * `Params::to_query_string()` is the PHP mirror, and the two have to build the
 * same query string. Otherwise a link the theme renders and a url the store
 * pushes are two cache keys for one search.
 */
export function toQueryString(
  query: string,
  params: SearchParamValues,
): URLSearchParams {
  const out = new URLSearchParams([["s", query]]);

  for (const name of declaredParams.keys()) {
    const value = params[name];
    for (const one of Array.isArray(value) ? value : [value]) {
      if (one === "" || one == null) continue;
      out.append(urlKey(name), String(one));
    }
  }

  return out;
}
